import { Link, NavLink } from 'react-router'
import { cn } from '@/shared/lib/utils'
import { BrandMark } from '@/shared/components/brand-mark'
import { useAuthStore } from '@/store/auth-store'
import { AccountMenu } from '@/shared/components/navigation/account-menu'
import { NotificationBell } from '@/shared/components/navigation/notification-bell'

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/enquiries', label: 'Enquiries' },
  { to: '/cases', label: 'Cases' },
  { to: '/invoices', label: 'Invoices' },
  { to: '/dsa-codes', label: 'DSA Codes' },
] as const


export function BrandHeader() {
  const profile = useAuthStore((s) => s.profile)

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-white/90 backdrop-blur-xl">
      <div className="relative mx-auto flex h-16 w-full max-w-[1800px] items-center px-4 md:px-6 lg:px-8 2xl:px-10">
        <Link
          to="/"
          aria-label="PSN Connect home"
          className="flex shrink-0 items-center rounded-lg text-navy-900"
        >
          <BrandMark tone="light" markClassName="h-8 w-8" />
        </Link>

        {/* Centred on the viewport from lg up. The current page is marked by a rule
            that sits on the header border, not by a filled pill. */}
        <nav
          aria-label="Main navigation"
          className="absolute left-1/2 hidden h-16 -translate-x-1/2 items-stretch gap-1 whitespace-nowrap lg:flex"
        >
          {navItems.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center px-4 text-sm tracking-tight transition-colors',
                  isActive
                    ? 'font-bold text-navy-900'
                    : 'font-medium text-neutral-500 hover:text-navy-900'
                )
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {label}
                  <span
                    aria-hidden="true"
                    data-active={isActive}
                    className={cn(
                      'absolute -bottom-1.5 left-0 right-0 h-[3px] rounded-full transition-all duration-200 ease-out',
                      isActive
                        ? 'scale-x-100 bg-brand-500'
                        : 'scale-x-0 bg-navy-200 group-hover:scale-x-100'
                    )}
                  />
                  {isActive && <span className="sr-only">, current page</span>}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex shrink-0 items-center gap-1.5 md:gap-2">
          {profile?.connectorCode && (
            <span className="hidden font-mono text-xs font-semibold tracking-tight text-neutral-500 md:inline">
              {profile.connectorCode}
            </span>
          )}

          {profile && (
            <>
              <div className="hidden lg:block">
                <NotificationBell />
              </div>
              <AccountMenu
                fullName={profile.displayName}
                pictureUrl={profile.profilePictureUrl}
              />
            </>
          )}
        </div>
      </div>
    </header>
  )
}
